import { useState, useEffect, useMemo, useRef } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import { format, addDays, addBusinessDays, differenceInDays } from 'date-fns';
import { calculateJoursOuvres } from '@/lib/heures-utils';
import { fr } from 'date-fns/locale';
import { Play, Users, Calendar, CheckCircle2, ChevronRight, ChevronLeft, AlertTriangle, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { useSuspension } from '@/hooks/useSuspension';
import { ModalSelectionEquipeRetard, SelectedMemberForDelay } from './ModalSelectionEquipeRetard';
import type {
  SuspensionInfo,
  RepriseFormData,
  SUSPENSION_RAISONS,
} from '@/types/calendar';

// Libellés des raisons
const RAISONS: Record<string, string> = {
  intemperies: 'Intempéries',
  attente_materiaux: 'Attente matériaux',
  probleme_client: 'Problème client',
  autre: 'Autre',
};

interface ModalRepriseChantierProps {
  isOpen: boolean;
  onClose: () => void;
  chantierId: string;
  chantierNom: string;
  suspensionInfo: SuspensionInfo;
  onConfirm: () => void;
  entrepriseId?: string;
}

type Step = 1 | 2 | 3 | 4;

export function ModalRepriseChantier({
  isOpen,
  onClose,
  chantierId,
  chantierNom,
  suspensionInfo,
  onConfirm,
  entrepriseId,
}: ModalRepriseChantierProps) {
  useModalProtection(isOpen);
  const [step, setStep] = useState<Step>(1);
  const [formData, setFormData] = useState<RepriseFormData>({
    type_reprise: 'equipe_origine',
    date_reprise: format(new Date(), 'yyyy-MM-dd'),
    date_fin: format(addDays(new Date(), 14), 'yyyy-MM-dd'),
    heures_par_jour: 8,
    membres_selectionnes: [],
    equipes_selectionnees: [],
  });

  // État pour les membres sélectionnés (nouvelle équipe)
  const [selectedMembers, setSelectedMembers] = useState<SelectedMemberForDelay[]>([]);

  const {
    loading,
    reprendreChantier,
  } = useSuspension({
    chantierId,
    initialSuspension: suspensionInfo,
    onSuccess: () => {
      onConfirm();
      onClose();
    },
  });

  // Réinitialiser au montage
  // Fix B2 : wasOpenRef empêche le reset du formulaire quand suspensionInfo change
  // en arrière-plan (causé par Realtime reconnexion après tab switch)
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && suspensionInfo && !wasOpenRef.current) {
      // Modal vient de s'ouvrir → initialiser
      setStep(1);
      // Pré-remplir les heures depuis le snapshot
      const heuresParJour = suspensionInfo.affectations_suspendues?.[0]?.heures_par_jour || 8;
      setFormData({
        type_reprise: 'equipe_origine',
        date_reprise: format(new Date(), 'yyyy-MM-dd'),
        date_fin: format(addBusinessDays(new Date(), 5), 'yyyy-MM-dd'), // 5 jours ouvrés
        heures_par_jour: heuresParJour,
        membres_selectionnes: [],
        equipes_selectionnees: [],
      });
      setSelectedMembers([]);
      wasOpenRef.current = true;
    }
    if (!isOpen) {
      wasOpenRef.current = false;
    }
  }, [isOpen, suspensionInfo]);

  // Dédupliquer affectations_suspendues par membre_equipe_id (si doublons dans le snapshot)
  const affectationsSuspenduesUniques = useMemo(() => {
    if (!suspensionInfo?.affectations_suspendues) return [];
    const seenIds = new Set<string>();
    return suspensionInfo.affectations_suspendues.filter(aff => {
      if (aff.membre_equipe_id && seenIds.has(aff.membre_equipe_id)) {
        return false;
      }
      if (aff.membre_equipe_id) {
        seenIds.add(aff.membre_equipe_id);
      }
      return true;
    });
  }, [suspensionInfo?.affectations_suspendues]);

  // Calculer la durée de suspension
  const joursSuspension = suspensionInfo
    ? differenceInDays(new Date(), new Date(suspensionInfo.date_suspension))
    : 0;

  // Calculer le nombre de jours de reprise
  const nbJoursReprise = formData.date_reprise && formData.date_fin
    ? differenceInDays(new Date(formData.date_fin), new Date(formData.date_reprise)) + 1
    : 0;

  // Vérifier si des membres en conflit sont sélectionnés
  const membresEnConflit = selectedMembers.filter(m => m.hasConflict);
  const hasConflicts = membresEnConflit.length > 0;

  // Validation de l'étape actuelle
  const isStepValid = (): boolean => {
    switch (step) {
      case 1:
        return true; // Choix équipe
      case 2:
        if (formData.type_reprise === 'nouvelle_equipe') {
          // Doit avoir des membres et pas de conflits
          return selectedMembers.length > 0 && !hasConflicts;
        }
        return formData.date_reprise && formData.date_fin;
      case 3:
        return true; // Conflits - on gère différemment
      case 4:
        // Bloquer si conflits pour nouvelle équipe
        if (formData.type_reprise === 'nouvelle_equipe' && hasConflicts) {
          return false;
        }
        return true; // Confirmation
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step < 4) {
      // Si équipe origine, on peut sauter l'étape 3 (conflits) pour l'instant
      if (step === 2 && formData.type_reprise === 'equipe_origine') {
        setStep(4);
      } else if (step === 2) {
        // Pour nouvelle équipe, on irait à l'étape conflits (simplifié ici)
        setStep(4);
      } else {
        setStep((step + 1) as Step);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      // Si on est à l'étape 4 et on vient de l'étape 2
      if (step === 4) {
        setStep(2);
      } else {
        setStep((step - 1) as Step);
      }
    }
  };

  const handleConfirm = async () => {
    // Calculer le nombre de jours ouvrés (exclut weekends)
    const nbJours = calculateJoursOuvres(formData.date_reprise, formData.date_fin);

    await reprendreChantier({
      ...formData,
      jours_travailles: Math.max(1, nbJours), // Minimum 1 jour
      membres_selectionnes: formData.type_reprise === 'nouvelle_equipe'
        ? selectedMembers.map(m => m.id)
        : undefined,
    });
  };

  // Handler pour la mise à jour des membres sélectionnés
  const handleMembersChange = (members: SelectedMemberForDelay[]) => {
    setSelectedMembers(members);
    setFormData(prev => ({
      ...prev,
      membres_selectionnes: members.map(m => m.id),
    }));
  };

  // Rendu des étapes
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Choix de l'équipe</h3>

            {/* Info suspension */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Suspendu depuis</span>
                <span className="font-medium">{joursSuspension} jours</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Raison</span>
                <span className="font-medium">{RAISONS[suspensionInfo?.raison] || suspensionInfo?.raison}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Équipe origine</span>
                <span className="font-medium">
                  {affectationsSuspenduesUniques.length} membre(s)
                </span>
              </div>
            </div>

            <RadioGroup
              value={formData.type_reprise}
              onValueChange={(value) => setFormData(prev => ({
                ...prev,
                type_reprise: value as 'equipe_origine' | 'nouvelle_equipe',
              }))}
              className="space-y-4"
            >
              {/* Équipe d'origine */}
              <div
                className={cn(
                  "flex items-start gap-4 p-4 rounded-lg border-2 transition-all cursor-pointer",
                  formData.type_reprise === 'equipe_origine'
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/50"
                )}
                onClick={() => setFormData(prev => ({ ...prev, type_reprise: 'equipe_origine' }))}
              >
                <RadioGroupItem value="equipe_origine" id="equipe_origine" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="equipe_origine" className="font-medium cursor-pointer flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Équipe d'origine
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Reprendre avec les mêmes membres qu'avant la suspension.
                  </p>
                  {/* Liste des membres */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {affectationsSuspenduesUniques.map((aff, i) => (
                      <span
                        key={aff.membre_equipe_id || i}
                        className="px-2 py-1 bg-muted rounded-full text-xs"
                      >
                        {aff.membre_prenom} {aff.membre_nom}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Nouvelle équipe */}
              <div
                className={cn(
                  "flex items-start gap-4 p-4 rounded-lg border-2 transition-all cursor-pointer",
                  formData.type_reprise === 'nouvelle_equipe'
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/50"
                )}
                onClick={() => setFormData(prev => ({ ...prev, type_reprise: 'nouvelle_equipe' }))}
              >
                <RadioGroupItem value="nouvelle_equipe" id="nouvelle_equipe" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="nouvelle_equipe" className="font-medium cursor-pointer flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Nouvelle équipe
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sélectionner de nouveaux membres ou équipes pour la reprise.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Configuration de la reprise</h3>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de reprise</Label>
                <Input
                  type="date"
                  value={formData.date_reprise}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    date_reprise: e.target.value,
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={formData.date_fin}
                  min={formData.date_reprise}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    date_fin: e.target.value,
                  }))}
                />
              </div>
            </div>

            {/* Heures par jour */}
            <div className="space-y-2">
              <Label>Heures par jour</Label>
              <Input
                type="number"
                value={formData.heures_par_jour}
                min={1}
                max={12}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  heures_par_jour: parseFloat(e.target.value) || 8,
                }))}
              />
            </div>

            {/* Affichage équipe origine */}
            {formData.type_reprise === 'equipe_origine' && (
              <div className="space-y-3">
                <Label>Membres à affecter</Label>
                <div className="border rounded-lg p-4 space-y-2">
                  {affectationsSuspenduesUniques.map((aff) => (
                    <div
                      key={aff.membre_equipe_id}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded"
                    >
                      <span>
                        {aff.membre_prenom} {aff.membre_nom}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {aff.taux_horaire}€/h
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sélection nouvelle équipe avec détection de conflits */}
            {formData.type_reprise === 'nouvelle_equipe' && (
              <>
                <ModalSelectionEquipeRetard
                  isOpen={true}
                  onClose={() => {}}
                  entrepriseId={entrepriseId}
                  chantierId={chantierId}
                  delayDays={nbJoursReprise}
                  dateDebutRetard={formData.date_reprise ? new Date(formData.date_reprise) : undefined}
                  dateFinRetard={formData.date_fin ? new Date(formData.date_fin) : undefined}
                  onConfirm={() => {}}
                  onChange={handleMembersChange}
                  selectedMembers={selectedMembers}
                  embedded={true}
                />

                {/* Avertissement si membres en conflit sélectionnés */}
                {hasConflicts && (
                  <div className="flex items-start gap-3 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg mt-4">
                    <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-orange-600">
                        {membresEnConflit.length} membre{membresEnConflit.length > 1 ? 's' : ''} déjà affecté{membresEnConflit.length > 1 ? 's' : ''}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Les membres suivants sont déjà affectés sur d'autres chantiers pendant cette période :
                      </p>
                      <ul className="text-sm mt-2 space-y-1">
                        {membresEnConflit.map(m => (
                          <li key={m.id} className="flex items-center gap-2">
                            <span className="font-medium">{m.prenom} {m.nom}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-orange-600">{m.conflictChantierNom}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-muted-foreground mt-2">
                        Désélectionnez-les ou attendez la fin de leur affectation actuelle.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 4:
        // Calculer le nombre de jours ouvrés (exclut weekends)
        const nbJours = Math.max(1, calculateJoursOuvres(formData.date_reprise, formData.date_fin));

        // Utiliser soit les membres sélectionnés (nouvelle équipe) soit l'équipe d'origine (dédupliquée)
        const membresReprise = formData.type_reprise === 'equipe_origine'
          ? affectationsSuspenduesUniques
          : selectedMembers.map(m => ({
              membre_prenom: m.prenom,
              membre_nom: m.nom,
              taux_horaire: m.taux_horaire,
            }));

        // Calculer coût estimé
        const coutEstime = formData.type_reprise === 'equipe_origine'
          ? affectationsSuspenduesUniques.reduce((sum, aff) => {
              return sum + (aff.taux_horaire * formData.heures_par_jour * nbJours);
            }, 0)
          : selectedMembers.reduce((sum, m) => {
              const coef = m.coefficient_charges || 1.85;
              const coutJournalier = m.taux_horaire * coef * formData.heures_par_jour;
              return sum + (coutJournalier * nbJours);
            }, 0);

        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Confirmation de la reprise</h3>

            {/* Récapitulatif */}
            <div className="bg-muted/50 rounded-lg divide-y divide-border">
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Type de reprise</span>
                <span className="font-medium">
                  {formData.type_reprise === 'equipe_origine' ? 'Équipe d\'origine' : 'Nouvelle équipe'}
                </span>
              </div>
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Date de reprise</span>
                <span className="font-medium">
                  {format(new Date(formData.date_reprise), 'dd MMMM yyyy', { locale: fr })}
                </span>
              </div>
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Date de fin</span>
                <span className="font-medium">
                  {format(new Date(formData.date_fin), 'dd MMMM yyyy', { locale: fr })}
                </span>
              </div>
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Jours travaillés</span>
                <span className="font-medium">{nbJours} jours</span>
              </div>
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Heures/jour</span>
                <span className="font-medium">{formData.heures_par_jour}h</span>
              </div>
            </div>

            {/* Membres */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Affectations à créer ({membresReprise.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {membresReprise.map((aff, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                  >
                    {aff.membre_prenom} {aff.membre_nom}
                  </span>
                ))}
              </div>
            </div>

            {/* Coût estimé */}
            <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div>
                <span className="font-medium">Coût main d'œuvre estimé</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {membresReprise.length} membre(s) × {formData.heures_par_jour}h × {nbJours}j
                </p>
              </div>
              <span className="text-lg font-bold text-green-500">
                {coutEstime.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
              </span>
            </div>

            {/* Info */}
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Le chantier sera remis en cours</p>
                <p className="text-muted-foreground mt-1">
                  Les affectations seront créées et les membres passés en statut "sur chantier".
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="w-5 h-5 text-green-500" />
            Reprendre le chantier
          </DialogTitle>
        </DialogHeader>

        {/* Indicateur d'étapes (simplifié : 1 -> 2 -> 4) */}
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 4].map((s, index) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all",
                  s === step
                    ? "bg-primary text-primary-foreground"
                    : s < step
                    ? "bg-green-500 text-white"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {s < step ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
              </div>
              {index < 2 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-2",
                    (index === 0 && step > 1) || (index === 1 && step === 4) ? "bg-green-500" : "bg-muted"
                  )}
                />
              )}
            </div>
          ))}
        </div>

        {/* Contenu de l'étape */}
        <div className="flex-1 overflow-y-auto pr-2">
          {renderStep()}
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={step === 1 ? onClose : handleBack}
            disabled={loading}
          >
            {step === 1 ? (
              'Annuler'
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Retour
              </>
            )}
          </Button>

          {step < 4 ? (
            <Button onClick={handleNext} disabled={!isStepValid()}>
              Suivant
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={loading}
              className="bg-green-500 hover:bg-green-600"
            >
              {loading ? 'Reprise...' : 'Confirmer la reprise'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ModalRepriseChantier;
